// FixedIncomePage.js - The Ultimate Brazilian Fixed Income Dashboard
import React, { useState, useEffect, useMemo } from 'react';
import { Header } from '../components/Header';
import Silk from '../components/Silk';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell 
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || '/api/go';

// Your specific DI Futures list
const BOND_TICKERS_LIST = [
  "DI1F27", "DI1F28", "DI1F29", "DI1F30", "DI1F31", "DI1F33", "DI1F32", "DI1F35",
  "DI1N27", "DI1J26", "DI1V26", "DI1J27", "DI1N28", "DI1N29", "DI1F37", "DI1N26",
  "DI1V27", "DI1N30", "DI1V28", "DI1J28", "DI1J29", "DI1Z26", "DI1F34", "DI1G27",
  "DI1V29", "DI1F36", "DI1J31", "DI1M26", "DI1J30", "DI1K26", "DI1U26", "DI1Q26",
  "DI1H26", "DI1X26", "DI1V30", "DI1Q27", "DI1V31", "DI1N31", "DI1F38", "DI1F41",
  "DI1F40", "DI1F39"
];
// ============================================
// MOCK DATA (Replace with real API calls)
// ============================================

const COPOM_OPTIONS_DATA = [
  { strike: 100.00, price: 5.00, volume: 313677, impliedSelic: 10.00, prob: 5 },
  { strike: 99.75, price: 25.00, volume: 394101, impliedSelic: 10.25, prob: 15 },
  { strike: 99.50, price: 63.00, volume: 517597, impliedSelic: 10.50, prob: 52 }, // CONSENSUS
  { strike: 99.25, price: 7.00, volume: 300061, impliedSelic: 10.75, prob: 23 },
  { strike: 99.00, price: 1.00, volume: 205000, impliedSelic: 11.00, prob: 5 },
];

const DI_FUTURES_CURVE = [
  { maturity: 'Jan/26', rate: 10.15, days: 30 },
  { maturity: 'Mar/26', rate: 10.45, days: 60 },
  { maturity: 'Jun/26', rate: 10.75, days: 150 },
  { maturity: 'Dez/26', rate: 11.20, days: 330 },
  { maturity: 'Jan/27', rate: 11.45, days: 365 },
  { maturity: 'Jan/28', rate: 11.80, days: 730 },
  { maturity: 'Jan/29', rate: 12.10, days: 1095 },
];

const FOCUS_EXPECTATIONS = [
  { week: '27/Jan', selic: 10.25, ipca: 4.5 },
  { week: '03/Fev', selic: 10.35, ipca: 4.6 },
  { week: '10/Fev', selic: 10.50, ipca: 4.7 },
  { week: 'HOJE', selic: 10.50, ipca: 4.65 },
];

const CDS_SPREADS = [
  { country: 'Brasil', spread1y: 125, spread5y: 185, rating: 'BB-', change: +5 },
  { country: 'México', spread1y: 85, spread5y: 110, rating: 'BBB', change: -2 },
  { country: 'Chile', spread1y: 65, spread5y: 95, rating: 'A', change: +1 },
  { country: 'Argentina', spread1y: 850, spread5y: 1250, rating: 'CCC', change: +50 },
  { country: 'Colômbia', spread1y: 145, spread5y: 210, rating: 'BB+', change: +8 },
];

const TREASURY_DIRECT = [
  { title: 'Tesouro Selic 2029', rate: 10.15, minInvestment: 150, liquidity: 'D+0' },
  { title: 'Tesouro Prefixado 2027', rate: 11.80, minInvestment: 35, liquidity: 'D+0' },
  { title: 'Tesouro IPCA+ 2035', rate: 'IPCA + 6.25%', minInvestment: 45, liquidity: 'D+0' },
  { title: 'Tesouro IPCA+ 2045', rate: 'IPCA + 6.45%', minInvestment: 40, liquidity: 'D+0' },
];

// ============================================
// COMPONENTS
// ============================================

// COPOM Options Probability Card
const COPOMProbabilityCard = ({ theme }) => {
  const maxProb = COPOM_OPTIONS_DATA.find(d => d.prob === Math.max(...COPOM_OPTIONS_DATA.map(x => x.prob)));

  return (
    <div style={{
      background: theme.cardBg,
      borderRadius: '16px',
      padding: '1.5rem',
      border: `2px solid rgba(212, 175, 55, 0.3)`,
      backdropFilter: 'blur(10px)',
      boxShadow: '0 8px 32px rgba(212, 175, 55, 0.1)'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '0.5rem'
        }}>
          <span style={{ fontSize: '1.5rem' }}>🏛️</span>
          <h3 style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#D4AF37', margin: 0 }}>
            Opções COPOM B3
          </h3>
          <span style={{
            background: 'rgba(212, 175, 55, 0.2)',
            color: '#D4AF37',
            padding: '4px 10px',
            borderRadius: '6px',
            fontSize: '0.7rem',
            fontWeight: 'bold'
          }}>
            EXCLUSIVO
          </span>
        </div>
        <p style={{ fontSize: '0.85rem', color: theme.textSecondary, margin: 0 }}>
          Mercado precifica a Selic através de opções legais da B3
        </p>
      </div>

      {/* Main Display */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1), rgba(139, 0, 0, 0.1))',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '0.9rem', color: theme.textSecondary, marginBottom: '0.5rem' }}>
            Consenso do Mercado (Próxima Reunião: 19 Mar 2026)
          </div>
          <div style={{
            fontSize: '4rem',
            fontWeight: 'bold',
            color: '#D4AF37',
            fontFamily: 'monospace',
            lineHeight: 1
          }}>
            {maxProb.impliedSelic}%
          </div>
          <div style={{
            fontSize: '1rem',
            color: '#ef4444',
            fontWeight: 'bold',
            marginTop: '0.5rem'
          }}>
            +50 bps vs atual (10.00%)
          </div>
        </div>
      </div>

      {/* Probability Chart */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{
          fontSize: '0.75rem',
          color: theme.textSecondary,
          marginBottom: '0.75rem',
          textTransform: 'uppercase',
          fontWeight: 'bold'
        }}>
          Distribuição de Probabilidade
        </div>
        
        <ResponsiveContainer width="100%" height={150}>
          <BarChart data={COPOM_OPTIONS_DATA}>
            <XAxis 
              dataKey="impliedSelic" 
              stroke={theme.textSecondary}
              fontSize={11}
              tickFormatter={(v) => `${v}%`}
            />
            <YAxis hide />
            <Tooltip
              contentStyle={{
                background: theme.cardBg,
                border: `1px solid ${theme.border}`,
                borderRadius: '8px'
              }}
              formatter={(value, name) => {
                if (name === 'prob') return [`${value}%`, 'Probabilidade'];
                return [value, name];
              }}
            />
            <Bar dataKey="prob" radius={[6, 6, 0, 0]}>
              {COPOM_OPTIONS_DATA.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.prob === maxProb.prob ? '#D4AF37' : 'rgba(212, 175, 55, 0.3)'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '1rem',
        paddingTop: '1rem',
        borderTop: `1px solid ${theme.border}`
      }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: theme.textSecondary }}>Selic Atual</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: theme.textPrimary }}>10.00%</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: theme.textSecondary }}>Focus</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: theme.textPrimary }}>10.50%</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: theme.textSecondary }}>Opções</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#D4AF37' }}>{maxProb.impliedSelic}%</div>
        </div>
      </div>
    </div>
  );
};

// DI Futures Curve Card
const DIFuturesCurveCard = ({ theme }) => (
  <div style={{
    background: theme.cardBg,
    borderRadius: '16px',
    padding: '1.5rem',
    border: `1px solid ${theme.border}`,
    backdropFilter: 'blur(10px)',
    height: '100%'
  }}>
    <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', color: theme.textPrimary, marginBottom: '0.5rem' }}>
      📊 Curva de DI Futuro
    </h3>
    <p style={{ fontSize: '0.85rem', color: theme.textSecondary, marginBottom: '1.5rem' }}>
      Expectativas de juros para diferentes vencimentos
    </p>

    <ResponsiveContainer width="100%" height={250}>
      <AreaChart data={DI_FUTURES_CURVE}>
        <defs>
          <linearGradient id="diGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis dataKey="maturity" stroke={theme.textSecondary} fontSize={10} />
        <YAxis domain={[10, 12.5]} stroke={theme.textSecondary} fontSize={10} />
        <Tooltip
          contentStyle={{ background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: '8px' }}
          formatter={(value) => [`${value.toFixed(2)}%`, 'Taxa']}
        />
        <Area type="monotone" dataKey="rate" stroke="#3b82f6" fillOpacity={1} fill="url(#diGrad)" strokeWidth={2} />
      </AreaChart>
    </ResponsiveContainer>

    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: '1rem',
      padding: '1rem',
      background: 'rgba(59, 130, 246, 0.1)',
      borderRadius: '8px'
    }}>
      <div>
        <div style={{ fontSize: '0.7rem', color: theme.textSecondary }}>Curto Prazo (Jan/26)</div>
        <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#3b82f6' }}>10.15%</div>
      </div>
      <div>
        <div style={{ fontSize: '0.7rem', color: theme.textSecondary }}>Longo Prazo (Jan/29)</div>
        <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#3b82f6' }}>12.10%</div>
      </div>
      <div>
        <div style={{ fontSize: '0.7rem', color: theme.textSecondary }}>Inclinação</div>
        <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#facc15' }}>+195 bps</div>
      </div>
    </div>
  </div>
);

// CDS Spreads Table
const CDSTable = ({ theme }) => (
  <div style={{
    background: theme.cardBg,
    borderRadius: '16px',
    padding: '1.5rem',
    border: `1px solid ${theme.border}`,
    backdropFilter: 'blur(10px)'
  }}>
    <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', color: theme.textPrimary, marginBottom: '0.5rem' }}>
      🌎 CDS Soberanos (LatAm)
    </h3>
    <p style={{ fontSize: '0.85rem', color: theme.textSecondary, marginBottom: '1.5rem' }}>
      Risco de crédito via Credit Default Swaps (ICE Clear Credit)
    </p>

    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
            <th style={{ textAlign: 'left', padding: '0.75rem', color: theme.textSecondary, fontWeight: '600', fontSize: '0.75rem' }}>PAÍS</th>
            <th style={{ textAlign: 'right', padding: '0.75rem', color: theme.textSecondary, fontWeight: '600', fontSize: '0.75rem' }}>1Y (bps)</th>
            <th style={{ textAlign: 'right', padding: '0.75rem', color: theme.textSecondary, fontWeight: '600', fontSize: '0.75rem' }}>5Y (bps)</th>
            <th style={{ textAlign: 'center', padding: '0.75rem', color: theme.textSecondary, fontWeight: '600', fontSize: '0.75rem' }}>RATING</th>
            <th style={{ textAlign: 'right', padding: '0.75rem', color: theme.textSecondary, fontWeight: '600', fontSize: '0.75rem' }}>VAR</th>
          </tr>
        </thead>
        <tbody>
          {CDS_SPREADS.map((item, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${theme.border}` }}>
              <td style={{ padding: '0.75rem', fontWeight: 'bold', color: theme.textPrimary }}>{item.country}</td>
              <td style={{ padding: '0.75rem', textAlign: 'right', fontFamily: 'monospace' }}>{item.spread1y}</td>
              <td style={{ padding: '0.75rem', textAlign: 'right', fontFamily: 'monospace' }}>{item.spread5y}</td>
              <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                <span style={{
                  background: item.rating.startsWith('A') ? 'rgba(74, 222, 128, 0.2)' : 
                              item.rating.startsWith('BBB') ? 'rgba(250, 204, 21, 0.2)' : 
                              'rgba(239, 68, 68, 0.2)',
                  color: item.rating.startsWith('A') ? '#4ade80' : 
                         item.rating.startsWith('BBB') ? '#facc15' : 
                         '#ef4444',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '0.8rem',
                  fontWeight: 'bold'
                }}>
                  {item.rating}
                </span>
              </td>
              <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold', color: item.change >= 0 ? '#ef4444' : '#4ade80' }}>
                {item.change >= 0 ? '+' : ''}{item.change}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Focus Expectations Card
const FocusExpectationsCard = ({ theme }) => (
  <div style={{
    background: theme.cardBg,
    borderRadius: '16px',
    padding: '1.5rem',
    border: `1px solid ${theme.border}`,
    backdropFilter: 'blur(10px)'
  }}>
    <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', color: theme.textPrimary, marginBottom: '0.5rem' }}>
      📈 Boletim Focus (BCB)
    </h3>
    <p style={{ fontSize: '0.85rem', color: theme.textSecondary, marginBottom: '1.5rem' }}>
      Expectativas do mercado coletadas pelo Banco Central
    </p>

    <ResponsiveContainer width="100%" height={200}>
      <LineChart data={FOCUS_EXPECTATIONS}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis dataKey="week" stroke={theme.textSecondary} fontSize={10} />
        <YAxis yAxisId="left" domain={[10, 11]} stroke={theme.textSecondary} fontSize={10} />
        <YAxis yAxisId="right" orientation="right" domain={[4, 5]} stroke={theme.textSecondary} fontSize={10} />
        <Tooltip
          contentStyle={{ background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: '8px' }}
        />
        <Line yAxisId="left" type="monotone" dataKey="selic" stroke="#D4AF37" strokeWidth={2} name="Selic" />
        <Line yAxisId="right" type="monotone" dataKey="ipca" stroke="#3b82f6" strokeWidth={2} name="IPCA" />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

// Treasury Direct Card
const TreasuryDirectCard = ({ theme }) => (
  <div style={{
    background: theme.cardBg,
    borderRadius: '16px',
    padding: '1.5rem',
    border: `1px solid ${theme.border}`,
    backdropFilter: 'blur(10px)'
  }}>
    <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', color: theme.textPrimary, marginBottom: '0.5rem' }}>
      🏦 Tesouro Direto
    </h3>
    <p style={{ fontSize: '0.85rem', color: theme.textSecondary, marginBottom: '1.5rem' }}>
      Títulos públicos disponíveis para pessoa física
    </p>

    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {TREASURY_DIRECT.map((bond, i) => (
        <div key={i} style={{
          padding: '1rem',
          background: 'rgba(0,0,0,0.2)',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <div style={{ fontWeight: 'bold', color: theme.textPrimary, marginBottom: '0.25rem' }}>
              {bond.title}
            </div>
            <div style={{ fontSize: '0.75rem', color: theme.textSecondary }}>
              Investimento mínimo: R$ {bond.minInvestment} • {bond.liquidity}
            </div>
          </div>
          <div style={{
            fontSize: '1.2rem',
            fontWeight: 'bold',
            color: '#4ade80',
            fontFamily: 'monospace'
          }}>
            {bond.rate}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ============================================
// MAIN PAGE
// ============================================

export default function FixedIncomePage() {
  const [isDark, setIsDark] = useState(true);
  const [marketData, setMarketData] = useState(null);
  const [loadingB3, setLoadingB3] = useState(true);

  // Fetch B3 Market Data (Same logic as MarketOverviewPage)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/overview`);
        const data = await response.json();
        setMarketData(data);
        setLoadingB3(false);
      } catch (err) {
        console.error('Error fetching B3 data:', err);
        setLoadingB3(false);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Filter the tickers for the rolling tape
  const getBondTickers = useMemo(() => {
    if (!marketData || !marketData.allTickers) return [];

    // Filter B3 data to only include the DI contracts in your list
    return marketData.allTickers
      .filter(item => BOND_TICKERS_LIST.includes(item.ticker))
      .map(t => ({
        ticker: t.ticker,
        name: t.name,
        ult: t.ult,
        var: t.var
      }));
  }, [marketData]);

  const theme = {
    background: isDark ? 'rgba(10, 10, 10, 0.95)' : 'rgba(240, 240, 240, 0.9)',
    textPrimary: isDark ? '#fff' : '#111',
    textSecondary: isDark ? '#a1a1aa' : '#555',
    cardBg: isDark ? 'rgba(20, 20, 20, 0.6)' : 'rgba(255, 255, 255, 0.8)',
    border: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0,0,0,0.1)',
  };

  return (
    <div style={{ minHeight: '100vh', background: isDark ? '#050505' : '#f0f0f0', color: theme.textPrimary, position: 'relative' }}>
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}>
        <Silk speed={15} scale={2} color={isDark ? "#2A5A2A" : "#e2e8f0"} noiseIntensity={isDark ? 3 : 1.5} rotation={0.3} />
      </div>

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* The Header now receives the filtered DI tickers */}
        <Header 
          theme={theme} 
          isDark={isDark} 
          toggleTheme={() => setIsDark(!isDark)} 
          topTickers={getBondTickers} 
        />

        <main style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem', marginTop: '2rem' }}>
            <h1 style={{ 
              fontSize: '4rem', 
              fontWeight: '900', 
              letterSpacing: '-2px', 
              marginBottom: '1rem',
              background: 'linear-gradient(to right, #D4AF37, #F0E68C)', 
              backgroundClip: 'text', 
              WebkitBackgroundClip: 'text', 
              color: 'transparent'
            }}>
              Renda Fixa
            </h1>
            <p style={{ fontSize: '1.2rem', color: theme.textSecondary, maxWidth: '700px', margin: '0 auto' }}>
              O dashboard definitivo de juros brasileiros. Opções COPOM, DI Futuro, CDS, Tesouro Direto e mais.
            </p>
          </div>

          {/* Section 1: COPOM + DI Curve */}
          <div style={{ marginBottom: '3rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
              <COPOMProbabilityCard theme={theme} />
              <DIFuturesCurveCard theme={theme} />
            </div>
          </div>

          {/* Section 2: CDS + Focus */}
          <div style={{ marginBottom: '3rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '2rem' }}>
              <CDSTable theme={theme} />
              <FocusExpectationsCard theme={theme} />
            </div>
          </div>

          {/* Section 3: Treasury Direct */}
          <div>
            <TreasuryDirectCard theme={theme} />
          </div>

        </main>
      </div>
    </div>
  );
}
